import React, { useState, useEffect, createContext, useContext, useMemo, useRef, ReactNode } from "react";
import ReactDOM from "react-dom";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { navigationConfig, NavItem } from "../config/navigation.ts";
import {
  MdMenu,
  MdChevronLeft,
  MdKeyboardArrowDown,
  MdPerson,
  MdLogout,
  MdSettings,
  MdDarkMode,
  MdLightMode,
  MdOutlineHome,
} from "react-icons/md";
import { FiClock, FiCalendar, FiBell, FiUser, FiLogOut } from "react-icons/fi";
import { FaWallet } from "react-icons/fa";
import { useTheme } from "../contexts/ThemeContext.tsx";
import { themes } from "../theme.ts";
import logo from "../assets/logo.png";

// Types
interface User {
  user?: {
    email?: string;
    role?: string;
    profile?: {
      organization_name?: string;
      first_name?: string;
    };
    id?: string | number;
  };
}

// Create a loading context
export const LoadingContext = createContext<{
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
}>({
  isLoading: false,
  setIsLoading: () => { },
});

export const useLoading = () => useContext(LoadingContext);

interface DashboardLayoutProps {
  children: ReactNode;
  userType: string;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  children,
  userType
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { themeName, setThemeName } = useTheme();
  const currentTheme = themes[themeName];

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [user, setUser] = useState<User | null>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);

  // Fetch user from localStorage
  useEffect(() => {
    try {
      const userString = localStorage.getItem("user");
      if (userString) {
        setUser(JSON.parse(userString));
      }
    } catch (error) {
      console.error("Failed to parse user data");
    }
  }, []);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setIsSidebarOpen(false);
      } else {
        setIsSidebarOpen(true);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close dropdowns on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setIsNotificationsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const navigation = useMemo(
    () => (userType ? navigationConfig[userType] : []),
    [userType]
  );

  const handleNavigation = (path: string) => {
    setIsLoading(true);
    navigate(path);
    setIsMobileMenuOpen(false);
    if (window.innerWidth < 1024) setIsSidebarOpen(false);
    setTimeout(() => setIsLoading(false), 500);
  };

  const toggleSubmenu = (path: string) => {
    setExpandedMenus(prev =>
      prev.includes(path) ? prev.filter(p => p !== path) : [...prev, path]
    );
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate("/login");
  };

  const formattedTime = currentTime.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  const formattedDate = currentTime.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });

  const getPageTitle = () => {
    const exactMatch = navigation.find(item => item.path === location.pathname);
    if (exactMatch) return exactMatch.title;

    const childMatch = navigation.find(item =>
      item.children?.some(child => child.path === location.pathname)
    );
    if (childMatch) {
      const child = childMatch.children?.find(c => c.path === location.pathname);
      return child?.title || childMatch.title;
    }
    return "Dashboard";
  };

  const loadingContextValue = useMemo(
    () => ({ isLoading, setIsLoading }),
    [isLoading]
  );

  // Toggle theme function
  const toggleTheme = () => {
    const newTheme = themeName === 'dark' ? 'light' : 'dark';
    setThemeName(newTheme);
  };

  // NavLink Component with theme support
  const NavLink = ({ item, level = 0 }: { item: NavItem; level?: number }) => {
    const Icon = item.icon || MdOutlineHome;
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedMenus.includes(item.path);
    const isActive = location.pathname === item.path;
    const isChildActive = item.children?.some(child => location.pathname === child.path);
    const [showDropdown, setShowDropdown] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const handleClick = (e: React.MouseEvent) => {
      e.preventDefault();
      if (hasChildren) {
        if (window.innerWidth < 1024 && !isSidebarOpen) {
          setShowDropdown(!showDropdown);
        } else {
          toggleSubmenu(item.path);
        }
      } else {
        handleNavigation(item.path);
      }
    };

    const handleMouseEnter = () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (!isSidebarOpen) {
        setShowDropdown(true);
      }
    };

    const handleMouseLeave = () => {
      timeoutRef.current = setTimeout(() => setShowDropdown(false), 200);
    };

    // Calculate dropdown position
    const getDropdownPosition = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        const dropdownWidth = 200;
        const dropdownHeight = Math.min((item.children?.length || 0) * 40, 300);

        let top = rect.top;
        let left = rect.right + 8;

        if (top + dropdownHeight > window.innerHeight - 20) {
          top = window.innerHeight - dropdownHeight - 20;
        }
        if (top < 10) {
          top = 10;
        }
        if (left + dropdownWidth > window.innerWidth - 10) {
          left = rect.left - dropdownWidth - 8;
        }
        if (left < 10) {
          left = 10;
        }

        return { top, left };
      }
      return { top: 0, left: 0 };
    };

    const { top, left } = getDropdownPosition();

    // For mobile view
    if (window.innerWidth < 768 && !isSidebarOpen && hasChildren && showDropdown) {
      return ReactDOM.createPortal(
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={() => setShowDropdown(false)}
        >
          <div
            className="w-full max-w-sm rounded-lg shadow-lg overflow-hidden"
            style={{
              backgroundColor: currentTheme.colors.surface,
              borderColor: currentTheme.colors.border,
              borderWidth: '1px'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="p-3 border-b"
              style={{
                borderColor: currentTheme.colors.border,
                color: currentTheme.colors.text
              }}
            >
              <h3 className="font-medium">{item.title}</h3>
            </div>
            <div className="p-2 max-h-[60vh] overflow-y-auto">
              {item.children.map((child) => (
                <button
                  key={child.path}
                  onClick={() => {
                    handleNavigation(child.path);
                    setShowDropdown(false);
                  }}
                  className="w-full text-left px-4 py-3 text-sm rounded-lg transition-colors"
                  style={{
                    color: location.pathname === child.path
                      ? currentTheme.colors.primary
                      : currentTheme.colors.text,
                    backgroundColor: location.pathname === child.path
                      ? `${currentTheme.colors.primary}20`
                      : 'transparent'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = `${currentTheme.colors.primary}10`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = location.pathname === child.path
                      ? `${currentTheme.colors.primary}20`
                      : 'transparent';
                  }}
                >
                  <div className="flex items-center space-x-3">
                    {child.icon && <child.icon className="text-base" />}
                    <span>{child.title}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>,
        document.body
      );
    }

    return (
      <div
        className="relative"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <button
          ref={buttonRef}
          onClick={handleClick}
          className="w-full flex items-center px-3 py-2 rounded-lg mb-0.5 transition-all duration-200 group"
          style={{
            color: isActive || isChildActive
              ? currentTheme.colors.primary
              : currentTheme.colors.textSecondary,
            backgroundColor: isActive || isChildActive
              ? `${currentTheme.colors.primary}20`
              : 'transparent'
          }}
          onMouseEnter={(e) => {
            if (!(isActive || isChildActive)) {
              e.currentTarget.style.backgroundColor = `${currentTheme.colors.primary}10`;
              e.currentTarget.style.color = currentTheme.colors.primary;
            }
          }}
          onMouseLeave={(e) => {
            if (!(isActive || isChildActive)) {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = currentTheme.colors.textSecondary;
            }
          }}
          title={!isSidebarOpen ? item.title : ""}
        >
          <div className="flex items-center w-full min-w-0">
            <Icon className={`text-xl flex-shrink-0 ${isSidebarOpen ? "mr-3" : "mx-auto"}`} />
            {isSidebarOpen && (
              <>
                <span className="flex-1 text-sm font-medium text-left truncate">{item.title}</span>
                {hasChildren && (
                  <MdKeyboardArrowDown
                    className={`flex-shrink-0 text-lg transition-transform duration-200 ml-1 ${isExpanded ? "rotate-180" : ""
                      }`}
                  />
                )}
              </>
            )}
          </div>
        </button>

        {/* Submenu for expanded state */}
        {hasChildren && isExpanded && isSidebarOpen && (
          <div
            className="mt-1 ml-9 pl-3 border-l-2"
            style={{ borderColor: `${currentTheme.colors.primary}40` }}
          >
            {item.children.map((child) => (
              <button
                key={child.path}
                onClick={() => handleNavigation(child.path)}
                className="w-full text-left px-3 py-2 text-sm rounded-lg mb-0.5 transition-colors truncate"
                style={{
                  color: location.pathname === child.path
                    ? currentTheme.colors.primary
                    : currentTheme.colors.textSecondary,
                  backgroundColor: location.pathname === child.path
                    ? `${currentTheme.colors.primary}20`
                    : 'transparent'
                }}
                onMouseEnter={(e) => {
                  if (location.pathname !== child.path) {
                    e.currentTarget.style.backgroundColor = `${currentTheme.colors.primary}10`;
                  }
                }}
                onMouseLeave={(e) => {
                  if (location.pathname !== child.path) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <div className="flex items-center space-x-3">
                  {child.icon && <child.icon className="text-base flex-shrink-0" />}
                  <span className="truncate">{child.title}</span>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Floating dropdown for collapsed sidebar */}
        {hasChildren && !isSidebarOpen && showDropdown && !(window.innerWidth < 768) && ReactDOM.createPortal(
          <div
            className="fixed z-[10000] min-w-[200px] rounded-lg shadow-lg border py-1"
            style={{
              top: top,
              left: left,
              backgroundColor: currentTheme.colors.surface,
              borderColor: currentTheme.colors.border,
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {item.children.map((child) => (
              <button
                key={child.path}
                onClick={() => {
                  handleNavigation(child.path);
                  setShowDropdown(false);
                }}
                className="w-full text-left px-4 py-2.5 text-sm transition-colors"
                style={{
                  color: location.pathname === child.path
                    ? currentTheme.colors.primary
                    : currentTheme.colors.text,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = `${currentTheme.colors.primary}10`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <div className="flex items-center space-x-3">
                  {child.icon && <child.icon className="text-base flex-shrink-0" />}
                  <span className="truncate">{child.title}</span>
                </div>
              </button>
            ))}
          </div>,
          document.body
        )}
      </div>
    );
  };

  return (
    <LoadingContext.Provider value={loadingContextValue}>
      <div
        className="flex h-screen overflow-hidden transition-colors duration-300"
        style={{
          backgroundColor: currentTheme.colors.background,
          color: currentTheme.colors.text
        }}
      >
        {/* Loading Overlay */}
        {isLoading && (
          <div
            className="fixed inset-0 flex items-center justify-center z-[9999] backdrop-blur-sm"
            style={{ backgroundColor: `${currentTheme.colors.background}CC` }}
          >
            <div
              className="w-10 h-10 border-2 rounded-full animate-spin"
              style={{
                borderColor: currentTheme.colors.primary,
                borderTopColor: 'transparent'
              }}
            />
          </div>
        )}

        {/* Mobile Menu Overlay */}
        {isMobileMenuOpen && (
          <div
            className="fixed inset-0 z-40 lg:hidden"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}

        {/* Sidebar */}
        <aside
          ref={sidebarRef}
          className={`${isSidebarOpen ? "w-64" : "w-20"
            } transition-all duration-300 fixed lg:relative h-full z-50 overflow-hidden ${isMobileMenuOpen ? "left-0" : "-left-64 lg:left-0"
            }`}
          style={{
            backgroundColor: currentTheme.colors.surface,
            borderRightWidth: '1px',
            borderRightColor: currentTheme.colors.border,
          }}
        >
          <div className="h-full flex flex-col">
            {/* Logo */}
            <div
              className="h-16 flex items-center px-4 border-b flex-shrink-0"
              style={{ borderColor: currentTheme.colors.border }}
            >
              {isSidebarOpen ? (
                <div className="flex items-center space-x-3 min-w-0">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    // style={{ backgroundColor: currentTheme.colors.primary }}
                  >
                    <img
                      src={logo}
                      alt="SocialsCollab Logo"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <span
                    className="font-semibold truncate"
                    style={{ color: currentTheme.colors.text }}
                  >
                    SocialsCollab
                  </span>
                </div>
              ) : (
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center mx-auto flex-shrink-0"
                  // style={{ backgroundColor: currentTheme.colors.primary }}
                >
                  <img
                    src={logo}
                    alt="SocialsCollab Logo"
                    className="w-full h-full object-contain"
                  />
                </div>
              )}
            </div>

            {/* Navigation */}
            <nav className="flex-1 overflow-y-auto overflow-x-hidden p-3">
              {navigation?.length > 0 ? (
                <div className="space-y-1">
                  {navigation.map((item) => (
                    <NavLink key={item.path} item={item} />
                  ))}
                </div>
              ) : (
                <div
                  className="text-center py-4 text-sm"
                  style={{ color: currentTheme.colors.textSecondary }}
                >
                  No navigation items
                </div>
              )}
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0 lg:ml-0 overflow-hidden">
          {/* Header */}
          <header
            className="h-16 sticky top-0 z-30 flex-shrink-0 transition-colors duration-300"
            style={{
              backgroundColor: currentTheme.colors.surface,
              borderBottomWidth: '1px',
              borderBottomColor: currentTheme.colors.border,
            }}
          >
            <div className="flex items-center justify-between h-full px-4 lg:px-6">
              <div className="flex items-center space-x-4 min-w-0">
                <button
                  onClick={() => setIsMobileMenuOpen(true)}
                  className="lg:hidden p-2 rounded-lg transition-colors flex-shrink-0"
                  style={{ color: currentTheme.colors.textSecondary }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = `${currentTheme.colors.primary}10`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <MdMenu size={20} />
                </button>
                <h1
                  className="text-lg font-medium truncate"
                  style={{ color: currentTheme.colors.text }}
                >
                  {getPageTitle()}
                </h1>
              </div>

              <div className="flex items-center space-x-3 flex-shrink-0">
                {/* Time */}
                <div
                  className="hidden md:flex items-center space-x-3 px-3 py-1.5 rounded-lg"
                  style={{
                    backgroundColor: `${currentTheme.colors.primary}10`,
                    color: currentTheme.colors.text
                  }}
                >
                  <div className="flex items-center space-x-1">
                    <FiClock style={{ color: currentTheme.colors.textSecondary }} size={14} />
                    <span className="text-sm">{formattedTime}</span>
                  </div>
                  <div
                    className="w-px h-4"
                    style={{ backgroundColor: currentTheme.colors.border }}
                  />
                  <div className="flex items-center space-x-1">
                    <FiCalendar style={{ color: currentTheme.colors.textSecondary }} size={14} />
                    <span className="text-sm">{formattedDate}</span>
                  </div>
                </div>

                {/* Theme Toggle */}
                <button
                  onClick={toggleTheme}
                  className="p-2 rounded-lg transition-colors"
                  style={{ color: currentTheme.colors.textSecondary }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = `${currentTheme.colors.primary}10`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  {themeName === 'dark' ? (
                    <MdLightMode className="text-xl" />
                  ) : (
                    <MdDarkMode className="text-xl" />
                  )}
                </button>

                {/* Notifications */}
                <div className="relative" ref={notificationsRef}>
                  <button
                    onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
                    className="relative p-2 rounded-lg transition-colors"
                    style={{ color: currentTheme.colors.textSecondary }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = `${currentTheme.colors.primary}10`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <FiBell className="text-xl" />
                    <span
                      className="absolute top-2 right-2 w-2 h-2 rounded-full"
                      style={{ backgroundColor: currentTheme.colors.error }}
                    />
                  </button>

                  {isNotificationsOpen && (
                    <div
                      className="absolute right-0 mt-2 w-80 rounded-lg shadow-lg border overflow-hidden"
                      style={{
                        backgroundColor: currentTheme.colors.surface,
                        borderColor: currentTheme.colors.border,
                      }}
                    >
                      <div
                        className="px-4 py-3 border-b"
                        style={{ borderColor: currentTheme.colors.border }}
                      >
                        <h3
                          className="text-sm font-medium"
                          style={{ color: currentTheme.colors.text }}
                        >
                          Notifications
                        </h3>
                      </div>
                      <div
                        className="p-8 text-center text-sm"
                        style={{ color: currentTheme.colors.textSecondary }}
                      >
                        No new notifications
                      </div>
                    </div>
                  )}
                </div>

                {/* Wallet */}
                {user?.user?.role === "ORGANIZATION" && user?.user?.id && (
                  <Link
                    to={`/dashboard/user/wallet/${user.user.id}`}
                    className="p-2 rounded-lg transition-colors"
                    style={{ color: currentTheme.colors.textSecondary }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = `${currentTheme.colors.primary}10`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <FaWallet className="text-xl" />
                  </Link>
                )}

                {/* Profile */}
                <div className="relative" ref={profileRef}>
                  <button
                    onClick={() => setIsProfileOpen(!isProfileOpen)}
                    className="flex items-center space-x-2 p-1.5 rounded-lg transition-colors border"
                    style={{
                      color: currentTheme.colors.textSecondary,
                      borderColor: currentTheme.colors.border,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = `${currentTheme.colors.primary}10`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <div
                      className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: currentTheme.colors.primary }}
                    >
                      <FiUser className="text-white" size={14} />
                    </div>
                    <span className="hidden lg:block text-sm">
                      {/* {user?.user?.email?.split('@')[0] || "Profile"} */}
                      Admin
                    </span>
                  </button>

                  {isProfileOpen && (
                    <div
                      className="absolute right-0 mt-2 w-56 rounded-lg shadow-lg border overflow-hidden"
                      style={{
                        backgroundColor: currentTheme.colors.surface,
                        borderColor: currentTheme.colors.border,
                      }}
                    >
                      <div
                        className="px-4 py-3 border-b"
                        style={{ borderColor: currentTheme.colors.border }}
                      >
                        <p
                          className="text-sm font-medium"
                          style={{ color: currentTheme.colors.text }}
                        >
                          {user?.user?.email || "User"}
                        </p>
                        <p
                          className="text-xs mt-0.5"
                          style={{ color: currentTheme.colors.textSecondary }}
                        >
                          {user?.user?.role || "Role"}
                        </p>
                      </div>
                      <div className="p-1">
                        <button
                          onClick={() => navigate('/dashboard/settings/theme')}
                          className="w-full flex items-center space-x-2 px-3 py-2 text-sm rounded transition-colors"
                          style={{ color: currentTheme.colors.text }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = `${currentTheme.colors.primary}10`;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <MdSettings size={16} />
                          <span>Settings</span>
                        </button>
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center space-x-2 px-3 py-2 text-sm rounded transition-colors"
                          style={{ color: currentTheme.colors.error }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = `${currentTheme.colors.error}10`;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <FiLogOut size={16} />
                          <span>Logout</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </header>

          {/* Main Content */}
          <main className="flex-1 overflow-auto p-4 lg:p-6">
            <div className="h-full">
              {children}
            </div>
          </main>
        </div>
      </div>
    </LoadingContext.Provider>
  );
};

export default DashboardLayout;